import React from "react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import {
  Users,
  Scale,
  Award,
  Shield,
  ArrowRight,
  Building2,
  Globe2,
  BookOpen,
  Handshake,
} from "lucide-react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";

const AboutUs = () => {
  const values = [
    {
      icon: <Scale className="h-8 w-8 text-legal-gold" />,
      title: "Excelencia Legal",
      description:
        "Comprometidos con los más altos estándares de práctica legal y servicio al cliente.",
    },
    {
      icon: <Shield className="h-8 w-8 text-legal-gold" />,
      title: "Integridad",
      description:
        "Actuamos con honestidad y transparencia en todas nuestras interacciones.",
    },
    {
      icon: <Users className="h-8 w-8 text-legal-gold" />,
      title: "Trabajo en Equipo",
      description:
        "Colaboramos para ofrecer soluciones legales integrales y efectivas.",
    },
    {
      icon: <Award className="h-8 w-8 text-legal-gold" />,
      title: "Innovación",
      description:
        "Utilizamos tecnología avanzada para mejorar nuestros servicios legales.",
    },
  ];

  const expertise = [
    {
      icon: <Building2 className="h-6 w-6" />,
      title: "Derecho Corporativo",
      description:
        "Asesoramiento integral para empresas en todas las etapas de su desarrollo.",
    },
    {
      icon: <Globe2 className="h-6 w-6" />,
      title: "Derecho Internacional",
      description: "Experiencia en transacciones y litigios internacionales.",
    },
    {
      icon: <BookOpen className="h-6 w-6" />,
      title: "Propiedad Intelectual",
      description:
        "Protección y gestión de activos intelectuales en la era digital.",
    },
    {
      icon: <Handshake className="h-6 w-6" />,
      title: "Resolución de Conflictos",
      description:
        "Mediación y arbitraje con enfoque en soluciones eficientes.",
    },
  ];

  const team = [
    {
      name: "Ana García",
      role: "Directora Legal",
      image:
        "https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e?auto=format&fit=crop&w=300&q=80",
    },
    {
      name: "Carlos Martínez",
      role: "Especialista en Tecnología Legal",
      image:
        "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?auto=format&fit=crop&w=300&q=80",
    },
    {
      name: "Laura Rodríguez",
      role: "Experta en IA Legal",
      image:
        "https://images.unsplash.com/photo-1580489944761-15a19d654956?auto=format&fit=crop&w=300&q=80",
    },
    {
      name: "David Torres",
      role: "Asesor Legal Senior",
      image:
        "https://images.unsplash.com/photo-1519085360753-af0119f7cbe7?auto=format&fit=crop&w=300&q=80",
    },
  ];

  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <main className="flex-grow">
        <div className="container mx-auto py-8 space-y-8">
          {/* Hero Section */}
          <section className="text-center space-y-4">
            <h1 className="text-4xl font-bold tracking-tight">
              Sobre Nosotros
            </h1>
            <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
              Somos una plataforma innovadora que combina la experiencia legal
              con la inteligencia artificial para proporcionar servicios legales
              accesibles y eficientes.
            </p>
          </section>

          <Separator className="my-8" />

          {/* Mission & Vision */}
          <div className="grid md:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle>Nuestra Misión</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground">
                  Democratizar el acceso a servicios legales mediante tecnología
                  avanzada, asegurando que cada persona tenga acceso a
                  asesoramiento legal de calidad de manera rápida y asequible.
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Nuestra Visión</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground">
                  Ser líderes en la transformación digital del sector legal,
                  creando un futuro donde la justicia sea más accesible y
                  eficiente para todos a través de la innovación tecnológica.
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Values Section */}
          <Card className="mt-8">
            <CardHeader>
              <CardTitle>Nuestros Valores</CardTitle>
              <CardDescription>
                Los principios que guían nuestro trabajo diario
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-6">
                <div className="space-y-2">
                  <h3 className="font-semibold">Innovación</h3>
                  <p className="text-sm text-muted-foreground">
                    Buscamos constantemente nuevas formas de mejorar y
                    simplificar los servicios legales a través de la tecnología.
                  </p>
                </div>
                <div className="space-y-2">
                  <h3 className="font-semibold">Accesibilidad</h3>
                  <p className="text-sm text-muted-foreground">
                    Nos esforzamos por hacer que los servicios legales sean
                    accesibles para todos, independientemente de su ubicación o
                    recursos.
                  </p>
                </div>
                <div className="space-y-2">
                  <h3 className="font-semibold">Excelencia</h3>
                  <p className="text-sm text-muted-foreground">
                    Mantenemos los más altos estándares de calidad en todos
                    nuestros servicios y soluciones.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Team Section */}
          <Card className="mt-8">
            <CardHeader>
              <CardTitle>Nuestro Equipo</CardTitle>
              <CardDescription>
                Un equipo multidisciplinario de expertos en derecho y tecnología
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-6">
                <div className="space-y-2">
                  <h3 className="font-semibold">Expertos Legales</h3>
                  <p className="text-sm text-muted-foreground">
                    Abogados con amplia experiencia en diferentes áreas del
                    derecho, comprometidos con la innovación legal.
                  </p>
                </div>
                <div className="space-y-2">
                  <h3 className="font-semibold">Ingenieros de IA</h3>
                  <p className="text-sm text-muted-foreground">
                    Especialistas en inteligencia artificial y aprendizaje
                    automático, desarrollando soluciones legales inteligentes.
                  </p>
                </div>
                <div className="space-y-2">
                  <h3 className="font-semibold">Diseñadores UX</h3>
                  <p className="text-sm text-muted-foreground">
                    Profesionales dedicados a crear experiencias de usuario
                    intuitivas y accesibles para nuestros servicios.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default AboutUs;
